/**
 * TableCard Component
 *
 * A card wrapper for data tables with header actions like export.
 * Provides consistent styling for table containers.
 */

import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii, shadows } from '../../tokens';

export interface TableCardProps {
    /** Card title */
    title?: string;
    /** Total count display text */
    totalText?: string;
    /** Action buttons (e.g., Export) */
    actions?: React.ReactNode;
    /** Table content */
    children: React.ReactNode;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const TableCard: React.FC<TableCardProps> = ({ title, totalText, actions, children, sx }) => {
    return (
        <Paper
            elevation={0}
            sx={{
                backgroundColor: colors.neutral[0],
                borderRadius: `${radii.lg}px`,
                border: `1px solid ${colors.neutral[200]}`,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                ...sx,
            }}>
            {/* Header */}
            {(title || actions) && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        py: 1.5,
                        minHeight: 36, // Consistent header height with or without actions
                        borderBottom: `1px solid ${colors.neutral[200]}`,
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {title && (
                            <Typography
                                sx={{
                                    fontSize: typographyTokens.fontSize.lg,
                                    fontWeight: typographyTokens.fontWeight.semibold,
                                    color: colors.neutral[900],
                                }}>
                                {title}
                            </Typography>
                        )}
                    </Box>
                    {actions && <Box>{actions}</Box>}
                </Box>
            )}

            {/* Info bar */}
            {totalText && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        py: 1.5,
                        backgroundColor: colors.neutral[0],
                    }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.sm,
                            color: colors.neutral[600],
                        }}>
                        {totalText}
                    </Typography>
                </Box>
            )}

            {/* Content */}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>
        </Paper>
    );
};

export default TableCard;
